import type { MaterialType, MaterialTypeSpec } from "@/api/materialTypeService";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Collapse,
  Button,
  CircularProgress,
  Chip,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import AddIcon from "@mui/icons-material/Add";
import { formattedDateTime } from "@/utils/date";
import React from "react";
import { usePermissions } from "@/hooks/usePermissions";

interface MaterialTypeManagementTableProps {
  data: MaterialType[];
  openRows: { [key: number]: boolean };
  setOpenRows: React.Dispatch<React.SetStateAction<{ [key: number]: boolean }>>;
  specsData: { [materialTypeId: number]: MaterialTypeSpec[] };
  isLoadingData: boolean;
  isLoadingSpecs: { [materialTypeId: number]: boolean };
  handleEdit: (materialType: MaterialType) => () => void;
  handleAddSpec: (materialTypeId: number, materialTypeName: string) => void;
  handleEditSpec: (spec: MaterialTypeSpec) => void;
}

const MaterialTypeManagementTable = ({
  data = [],
  openRows,
  setOpenRows,
  specsData,
  isLoadingData,
  isLoadingSpecs,
  handleEdit,
  handleAddSpec,
  handleEditSpec,
}: MaterialTypeManagementTableProps) => {
  const canEdit = usePermissions().canEdit("master.material");

  const toggleRow = (id: number) => {
    setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <Paper>
      <TableContainer sx={{ width: "100%", mt: 2, maxHeight: "70vh" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={56}></TableCell>
              <TableCell>Material Type</TableCell>
              <TableCell align="right">Specs</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell>Create By</TableCell>
              <TableCell>Create At</TableCell>
              <TableCell>Updated By</TableCell>
              <TableCell>Updated At</TableCell>
              <TableCell width="100px" align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoadingData ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      outline: "none",
                    }}
                  >
                    <CircularProgress size={60} thickness={4} color="primary" />
                  </Box>{" "}
                </TableCell>
              </TableRow>
            ) : (
              data?.map((t) => {
                return (
                  <React.Fragment key={t.id}>
                    <TableRow
                      onClick={() => toggleRow(t.id)}
                      hover
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell sx={{ borderLeft: "1px solid #919191" }}>
                        <IconButton size="small">
                          {openRows[t.id] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography fontWeight={600}>
                            {t.material_type_name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        {t.specs_count > 0 ? (
                          <Typography fontWeight={500} color="success">
                            {t.specs_count}
                          </Typography>
                        ) : (
                          <Typography fontWeight={500} color="error">
                            0
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}
                        >
                          <Chip
                            label={t.is_active ? "Active" : "Inactive"}
                            sx={{
                              backgroundColor: t.is_active
                                ? "success.light"
                                : "error.light",
                              color: "#fff",
                              fontWeight: "600",
                              fontSize: "0.8rem",
                              width: "80px",
                            }}
                            key={t.id}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>{t.created_by ? t.created_by : "-"}</TableCell>
                      <TableCell>
                        {t.created_at ? formattedDateTime(t.created_at) : "-"}
                      </TableCell>
                      <TableCell>{t.updated_by ? t.updated_by : "-"}</TableCell>
                      <TableCell>
                        {t.created_at ? formattedDateTime(t.updated_at) : "-"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ borderRight: "1px solid #919191" }}
                      >
                        <Tooltip
                          title={"แก้ไข"}
                          placement="top"
                          slotProps={{
                            popper: {
                              modifiers: [
                                {
                                  name: "offset",
                                  options: { offset: [0, -10] },
                                },
                              ],
                            },
                          }}
                        >
                          <IconButton
                            disabled={!canEdit}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(t)();
                            }}
                          >
                            <BorderColorIcon
                              color={canEdit ? "primary" : "disabled"}
                            />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* Collapse: specs */}
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        sx={{
                          p: 0,
                          border: 0,
                          borderLeft: "1px solid #919191",
                          borderRight: "1px solid #919191",
                          borderBottom: "1px solid #919191",
                        }}
                      >
                        <Collapse
                          in={openRows[t.id]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ p: 2, bgcolor: "#fafafa" }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ mb: 1 }}
                            >
                              <Typography variant="subtitle1" fontWeight={600}>
                                Specs of {t.material_type_name}
                              </Typography>
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                  handleAddSpec(t.id, t.material_type_name)
                                }
                                disabled={!canEdit}
                              >
                                Add Spec
                              </Button>
                            </Stack>
                            <Table className="secondary" size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Spec Name</TableCell>
                                  <TableCell align="center">Active</TableCell>
                                  <TableCell>Create By</TableCell>
                                  <TableCell>Create At</TableCell>
                                  <TableCell>Update By</TableCell>
                                  <TableCell>Update At</TableCell>
                                  <TableCell align="center">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {isLoadingSpecs[t.id] ? (
                                  <TableRow sx={{ backgroundColor: "#ffffff" }}>
                                    <TableCell
                                      colSpan={7}
                                      align="center"
                                      sx={{ py: 3 }}
                                    >
                                      <Box
                                        sx={{
                                          height: "100%",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          outline: "none",
                                        }}
                                      >
                                        <CircularProgress
                                          size={60}
                                          thickness={4}
                                          color="primary"
                                        />
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  <>
                                    {(specsData[t.id] || []).map(
                                      (
                                        spec: MaterialTypeSpec,
                                        index: number
                                      ) => (
                                        <TableRow
                                          sx={{ backgroundColor: "#ffffff" }}
                                          key={spec.id || index}
                                          hover
                                        >
                                          <TableCell>
                                            {spec.spec_name || "-"}
                                          </TableCell>
                                          <TableCell align="center">
                                            <Chip
                                              size="small"
                                              label={
                                                spec.is_active
                                                  ? "Active"
                                                  : "Inactive"
                                              }
                                              variant="outlined"
                                              color={
                                                spec.is_active
                                                  ? "success"
                                                  : "error"
                                              }
                                            />
                                          </TableCell>
                                          <TableCell>
                                            {spec.created_by || "-"}
                                          </TableCell>
                                          <TableCell>
                                            {spec.created_at
                                              ? formattedDateTime(
                                                  spec.created_at
                                                )
                                              : "-"}
                                          </TableCell>
                                          <TableCell>
                                            {spec.updated_by || "-"}
                                          </TableCell>
                                          <TableCell>
                                            {spec.updated_at
                                              ? formattedDateTime(
                                                  spec.updated_at
                                                )
                                              : "-"}
                                          </TableCell>
                                          <TableCell align="center">
                                            <Tooltip
                                              title={"แก้ไข"}
                                              placement="top"
                                              slotProps={{
                                                popper: {
                                                  modifiers: [
                                                    {
                                                      name: "offset",
                                                      options: {
                                                        offset: [0, -12],
                                                      },
                                                    },
                                                  ],
                                                },
                                              }}
                                            >
                                              <IconButton
                                                size="small"
                                                disabled={!canEdit}
                                                onClick={() =>
                                                  handleEditSpec(spec)
                                                }
                                              >
                                                <BorderColorIcon
                                                  fontSize="small"
                                                  color={
                                                    canEdit
                                                      ? "primary"
                                                      : "disabled"
                                                  }
                                                />
                                              </IconButton>
                                            </Tooltip>
                                          </TableCell>
                                        </TableRow>
                                      )
                                    )}
                                    {(!specsData[t.id] ||
                                      specsData[t.id].length === 0) &&
                                      !isLoadingSpecs[t.id] && (
                                        <TableRow>
                                          <TableCell
                                            colSpan={9}
                                            align="center"
                                            sx={{
                                              color: "text.secondary",
                                              backgroundColor: "#ffffff",
                                              py: 5,
                                              fontStyle: "italic",
                                            }}
                                          >
                                            No specs available
                                          </TableCell>
                                        </TableRow>
                                      )}
                                  </>
                                )}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default MaterialTypeManagementTable;
